import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, FloppyDisk } from "@phosphor-icons/react";

export default function LogEquipmentMetricsModal({ equipmentId, equipmentName, initialDate, isLegacyAuto, autoSnapshot, onClose, onSaved }) {
    const [f, setF] = useState({
        metric_date: initialDate || new Date().toISOString().slice(0, 10),
        operating_hrs: autoSnapshot?.operating_hrs ?? "",
        planned_downtime_hrs: autoSnapshot?.planned_downtime_hrs ?? "",
        unplanned_downtime_hrs: autoSnapshot?.unplanned_downtime_hrs ?? "",
        mttr_hrs: "",
        mtbf_hrs: "",
        availability_pct: "",
        utilisation_pct: "",
        reliability_pct: "",
        notes: "",
    });
    const [saving, setSaving] = useState(false);
    const [overrideExpand, setOverrideExpand] = useState(false);

    const update = (k, v) => setF((s) => ({ ...s, [k]: v }));

    const submit = async () => {
        if (!f.metric_date) {
            toast.error("Date is required");
            return;
        }
        const payload = {
            metric_date: f.metric_date,
            operating_hrs: parseFloat(f.operating_hrs) || 0,
            planned_downtime_hrs: parseFloat(f.planned_downtime_hrs) || 0,
            unplanned_downtime_hrs: parseFloat(f.unplanned_downtime_hrs) || 0,
            mttr_hrs: parseFloat(f.mttr_hrs) || 0,
            mtbf_hrs: parseFloat(f.mtbf_hrs) || 0,
            notes: f.notes || "",
        };
        ["availability_pct", "utilisation_pct", "reliability_pct"].forEach((k) => {
            if (f[k] !== "" && f[k] != null && !isNaN(parseFloat(f[k]))) {
                payload[k] = parseFloat(f[k]);
            }
        });
        try {
            setSaving(true);
            await api.post(`/equipment/${equipmentId}/metrics`, payload);
            onSaved && onSaved();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[90] bg-slate-900/85 flex items-start sm:items-center justify-center p-2 overflow-y-auto" data-testid="log-equipment-modal">
            <div className="bg-white rounded-lg w-full max-w-xl shadow-xl border border-slate-200 relative">
                <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="log-equipment-close"><X size={16} /></button>
                <div className="px-5 py-4 border-b border-slate-200">
                    <div className="uppercase-label text-[10px] text-[var(--grindrod-red)]">Log Daily Performance</div>
                    <h3 className="font-display font-black text-xl text-[var(--grindrod-navy)]">{equipmentName}</h3>
                    {isLegacyAuto && (
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2 mt-2">
                            Manual entry will override the auto-calculated metrics for this date.
                        </div>
                    )}
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Date">
                        <input type="date" value={f.metric_date} onChange={(e) => update("metric_date", e.target.value)} className="g-input" data-testid="metric-date" />
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Operating hours">
                            <input type="number" step="0.1" min="0" max="24" value={f.operating_hrs} onChange={(e) => update("operating_hrs", e.target.value)} className="g-input tabular" placeholder="18.6" data-testid="metric-operating" />
                        </Field>
                        <Field label="Planned downtime (hrs)">
                            <input type="number" step="0.1" min="0" max="24" value={f.planned_downtime_hrs} onChange={(e) => update("planned_downtime_hrs", e.target.value)} className="g-input tabular" placeholder="1.4" data-testid="metric-planned" />
                        </Field>
                        <Field label="Unplanned downtime (hrs)">
                            <input type="number" step="0.1" min="0" max="24" value={f.unplanned_downtime_hrs} onChange={(e) => update("unplanned_downtime_hrs", e.target.value)} className="g-input tabular" placeholder="1.0" data-testid="metric-unplanned" />
                        </Field>
                        <Field label="MTTR (hrs)">
                            <input type="number" step="0.1" min="0" value={f.mttr_hrs} onChange={(e) => update("mttr_hrs", e.target.value)} className="g-input tabular" placeholder="0.8" data-testid="metric-mttr" />
                        </Field>
                        <Field label="MTBF (hrs)">
                            <input type="number" step="0.1" min="0" value={f.mtbf_hrs} onChange={(e) => update("mtbf_hrs", e.target.value)} className="g-input tabular" placeholder="62.7" data-testid="metric-mtbf" />
                        </Field>
                    </div>

                    <button type="button" onClick={() => setOverrideExpand((s) => !s)} className="text-xs underline text-[var(--grindrod-navy)]" data-testid="toggle-pct-overrides">
                        {overrideExpand ? "Hide" : "Show"} percentage overrides (Availability / Utilisation / Reliability) — leave blank to auto-compute
                    </button>
                    {overrideExpand && (
                        <div className="grid grid-cols-3 gap-3 bg-slate-50 border border-slate-200 p-3 rounded-sm">
                            <Field label="Availability % override">
                                <input type="number" step="0.1" min="0" max="100" value={f.availability_pct} onChange={(e) => update("availability_pct", e.target.value)} className="g-input tabular" placeholder="92.4" data-testid="metric-availability" />
                            </Field>
                            <Field label="Utilisation % override">
                                <input type="number" step="0.1" min="0" max="100" value={f.utilisation_pct} onChange={(e) => update("utilisation_pct", e.target.value)} className="g-input tabular" placeholder="87.6" data-testid="metric-utilisation" />
                            </Field>
                            <Field label="Reliability % override">
                                <input type="number" step="0.1" min="0" max="100" value={f.reliability_pct} onChange={(e) => update("reliability_pct", e.target.value)} className="g-input tabular" placeholder="91.1" data-testid="metric-reliability" />
                            </Field>
                        </div>
                    )}

                    <Field label="Notes (optional)">
                        <textarea rows={2} value={f.notes} onChange={(e) => update("notes", e.target.value)} className="g-input" placeholder="Comments on the day…" data-testid="metric-notes" />
                    </Field>
                </div>
                <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
                    <button onClick={onClose} className="text-sm px-3 py-2 rounded-sm bg-slate-100 hover:bg-slate-200">Cancel</button>
                    <button onClick={submit} disabled={saving} className="text-sm px-3 py-2 rounded-sm bg-[var(--grindrod-navy)] text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="metric-save-btn">
                        <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1">{label}</span>
            {children}
        </label>
    );
}
